import { Injectable } from '@angular/core';

import { TextTrackModes } from './TextTrackModes';
import { TextTrackKinds } from './TextTrackKinds';

export interface IVbTextTrack extends TextTrack {
	vb?: boolean;
}


/**
	 * If an existing track with the given label exists, it is returned. Otherwise, a new track is created. Since the API lacks a means of removing a track, they must be reused.
	 * @param  HTMLMediaElement videoElement 	The video element to which we will add or reuse an existing text track.
	 * @param  TextTrackKinds kind         				How the text track is meant to be used. Possible values: 'subtitles' (default), 'captions', 'descriptions', 'chapters', 'metadata'.
	 * @param  string label        				User-readable title of the text track.
	 * @param  boolean flagAsVb					Indicate that it is a vb created track.
	 * @return HTMLTrackElement	   				Returns either an existing track matching the given label or newly created track.
	 */
export function getOrCreateTrack(videoElement: HTMLVideoElement, kind: TextTrackKinds, label: string, isVb: boolean = true): IVbTextTrack {
	return getTrack(videoElement, kind, label, isVb) || createTrack(videoElement, kind, label, isVb);
}

export function getTrack(videoElement: HTMLMediaElement, kind: TextTrackKinds, label: string, isVb: boolean = false): IVbTextTrack { /*jshint -W018 */
	const textTracks = videoElement.textTracks || []; //not an Array

	return Array.from(textTracks)
		.find(track => !!track.vb === isVb && track.label === label); //attempt to locate an existing track
}

export function addCueToTrack(textTrack: TextTrack, cue: VTTCue): void {
	textTrack.addCue(cue);
}

/**
	 * Create a new text track on the given video element.
	 * @param  HTMLMediaElement videoElement 	The video element on which a new text track will be created.
	 * @param  TextTrackKinds kind         				How the text track is meant to be used. Possible values: 'subtitles' (default), 'captions', 'descriptions', 'chapters', 'metadata'.
	 * @param  string label        				User-readable title of the text track.
	 * @param  boolean flagAsVb					Indicate that it is a vb created track.
	 * @return HTMLTrackElement	   				Returns the newly created track.
	 */
export function createTrack(videoElement: HTMLVideoElement, kind: TextTrackKinds, label: string, flagAsVb: boolean = true, lang?: string): IVbTextTrack {
	const track = videoElement.addTextTrack(kind, label, lang) as IVbTextTrack;

	track.vb = flagAsVb;

	return track;
}

/**
	 * Removes all cues from the given text track.
	 * @param  HTMLTrackElement textTrack 	A text track to be cleared of its cues.
	 */
export function clearCues(textTrack: TextTrack): void {
	const mode = textTrack.mode;
	if (mode === TextTrackModes.DISABLED) {
		textTrack.mode = TextTrackModes.HIDDEN;
	}

	if (textTrack.cues) {
		for (let cueIndex = textTrack.cues.length - 1; cueIndex >= 0; --cueIndex) {
			textTrack.removeCue(textTrack.cues[cueIndex]);
		}
	}

	if (mode === TextTrackModes.DISABLED) {
		textTrack.mode = TextTrackModes.DISABLED;
	}
}


@Injectable()
export class TextTrackHelperService {
	private TEXT_TRACK_CUE_CLASS: any;

	constructor() {
		this.TEXT_TRACK_CUE_CLASS = window.VTTCue || window.TextTrackCue; //TextTrackCue is the old API used by IE
	}

	public get isNativeTextTrackSupport(): boolean {
		return !!this.TEXT_TRACK_CUE_CLASS;
	}

	public getOrCreateTrack(videoElement: HTMLVideoElement, kind: TextTrackKinds, label: string, isVb: boolean = true): IVbTextTrack {
		return getOrCreateTrack(videoElement, kind, label, isVb);
	}

	public getTrack(videoElement: HTMLMediaElement, kind: TextTrackKinds, label: string, isVb: boolean = false): IVbTextTrack { /*jshint -W018 */
		return getTrack(videoElement, kind, label, isVb);
	}

	public createTrack(videoElement: HTMLVideoElement, kind: TextTrackKinds, label: string, flagAsVb: boolean = true): IVbTextTrack {
		return createTrack(videoElement, kind, label, flagAsVb);
	}

	public clearCues(textTrack: TextTrack): void {
		clearCues(textTrack);
	}

	/**
	 * Creates and adds a cue on to the given text track.
	 * @param TextTrack textTrack      [description]
	 * @param number startTimeInSec Start time for the cue in seconds.
	 * @param number endTimeInSec   End time for the cue in seconds.
	 * @param string text           Text to be displayed on visual cues. For non-visual cues, provide an empty string to keep browsers (such as FF) happy.
	 */
	public addCue(textTrack: TextTrack, startTimeInSec: number, endTimeInSec: number, text: string) {
		const cue = new this.TEXT_TRACK_CUE_CLASS(startTimeInSec, endTimeInSec, text);

		addCueToTrack(textTrack, cue);

		return cue;
	}

	/**
	 * Creates and adds a cue to the given text track.
	 * @param TextTrack textTrack    Text track that will receive the new cue.
	 * @param number startTimeInSec 		Start time for the cue in seconds.
	 * @param number endTimeInSec   		End time for the cue in seconds.
	 * @param string text 					Text to be displayed on visual cues. For non-visual cues, provide an empty string to keep browsers (such as FF) happy.
	 * @param any data						Data object to be attached to the cue.
	 */
	public addDataCue(textTrack: TextTrack, startTimeInSec: number, endTimeInSec: number, text: string, data: any) {
		const cue = new this.TEXT_TRACK_CUE_CLASS(startTimeInSec, endTimeInSec, text);

		//our own data payload
		cue.data = data;

		addCueToTrack(textTrack, cue);
	}

	/**
	 * Hides all text tracks on the given video element from the user.
	 * @param  HTMLMediaElement videoElement The video element on which we will hide all tracks.
	 */
	public hideAllTracks(videoElement: HTMLMediaElement): void {
		const textTracks = videoElement.textTracks || [];

		Array.from(textTracks)
			.forEach(textTrack => {
				textTrack.oncuechange = undefined;
				textTrack.mode = TextTrackModes.DISABLED;
			});
	}
}
